/**
 * DEVLOGS-TEMP — HTTP delivery. Batched, bounded, single-flight, NO RETRIES.
 *
 * Modelled on `createLogDrain` (artifacts/api-server/src/lib/logger.ts), whose
 * header states the rule this inherits: retrying logs is how a telemetry outage
 * becomes an application outage.
 */

import type { DevLogBatch } from "./types.ts";

/** Set on every outbound request so the fetch capture can recognise its own traffic. */
export const DEVLOG_SENTINEL_HEADER = "x-omnimedia-devlog";

export interface DevLogSendResult {
  ok: boolean;
  status?: number;
  /**
   * The server said "stop, permanently" (410, or a body with disabled:true).
   *
   * This is the only remote off-switch that can reach a kiosk. Removing an env
   * var stops the API server instantly but CANNOT reach a TV box or a desktop
   * kiosk for hours, so without this a bad setting is unfixable in the field.
   */
  disabled?: boolean;
}

export interface DevLogTransport {
  send(batch: DevLogBatch): Promise<DevLogSendResult>;
  /** Best-effort synchronous delivery at teardown. */
  sendSync?(batch: DevLogBatch): boolean;
}

export interface HttpTransportOptions {
  url: string;
  token?: string | null;
  /**
   * MUST be captured BEFORE any fetch wrapper is installed, or the transport
   * logs its own requests and the process never terminates.
   */
  fetchImpl: typeof fetch;
  timeoutMs?: number;
  maxBodyBytes?: number;
  /** navigator.sendBeacon, when the host has one. */
  beacon?: ((url: string, body: string) => boolean) | null;
  /** Called around a send so the fetch capture can skip re-entrant traffic. */
  onInFlight?: (inFlight: boolean) => void;
}

const DEFAULT_TIMEOUT_MS = 5000;
const DEFAULT_MAX_BODY_BYTES = 512_000;
const BEACON_MAX_BYTES = 64_000;

export function createHttpTransport(
  options: HttpTransportOptions,
): DevLogTransport {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const maxBodyBytes = options.maxBodyBytes ?? DEFAULT_MAX_BODY_BYTES;
  const doFetch = options.fetchImpl;

  function headers(): Record<string, string> {
    const out: Record<string, string> = {
      "content-type": "application/json",
      [DEVLOG_SENTINEL_HEADER]: "1",
    };
    if (options.token) out["authorization"] = `Bearer ${options.token}`;
    return out;
  }

  function encode(batch: DevLogBatch): string | null {
    let body: string;
    try {
      body = JSON.stringify(batch);
    } catch {
      return null;
    }
    if (body.length > maxBodyBytes) {
      // Halve the record list rather than dropping the batch entirely: the
      // envelope alone still carries seq range, so the loss stays visible.
      const half = Math.max(1, Math.floor(batch.rec.length / 2));
      try {
        body = JSON.stringify({ ...batch, rec: batch.rec.slice(0, half) });
      } catch {
        return null;
      }
      if (body.length > maxBodyBytes) return null;
    }
    return body;
  }

  return {
    async send(batch: DevLogBatch): Promise<DevLogSendResult> {
      const body = encode(batch);
      if (body === null) return { ok: false };

      // Explicit AbortController rather than AbortSignal.timeout: the latter is
      // not available on every Hermes build (see artifacts/tv/lib/crash-reporting.ts).
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | null = null;
      try {
        timer = setTimeout(() => {
          try {
            controller.abort();
          } catch {
            /* fire-and-forget */
          }
        }, timeoutMs);
        (timer as { unref?: () => void }).unref?.();
      } catch {
        timer = null;
      }

      options.onInFlight?.(true);
      try {
        const res = await doFetch(options.url, {
          method: "POST",
          headers: headers(),
          body,
          signal: controller.signal,
          keepalive: body.length < BEACON_MAX_BYTES,
        });
        if (res.status === 410) return { ok: false, status: 410, disabled: true };
        if (!res.ok) return { ok: false, status: res.status };
        const ack = await readIngestAck(res);
        // A 2xx is NOT proof of delivery. The real ingest endpoint always
        // acknowledges with a batch_id; a wrong endpoint (a health route, a
        // proxy splash page, a captive portal) answers 200 with anything else.
        // Counting those as sent made a total outage look healthy on the one
        // diagnostic surface built to catch it — observed live when a bare
        // Worker origin sent every desktop batch into the health route.
        return { ok: ack.delivered, status: res.status, disabled: ack.disabled };
      } catch {
        return { ok: false };
      } finally {
        options.onInFlight?.(false);
        if (timer !== null) clearTimeout(timer);
      }
    },

    sendSync(batch: DevLogBatch): boolean {
      const body = encode(batch);
      if (body === null || body.length > BEACON_MAX_BYTES) return false;
      const beacon = options.beacon;
      if (!beacon) return false;
      try {
        // sendBeacon cannot carry an Authorization header; the token rides in
        // the body for this path and the Worker accepts either.
        const withToken = options.token
          ? JSON.stringify({ ...JSON.parse(body), t: options.token })
          : body;
        return beacon(options.url, withToken);
      } catch {
        return false;
      }
    },
  };
}

async function readIngestAck(
  res: Response,
): Promise<{ delivered: boolean; disabled: boolean | undefined }> {
  try {
    const text = await res.text();
    if (text.length === 0 || text.length > 4096) {
      return { delivered: false, disabled: undefined };
    }
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== "object") {
      return { delivered: false, disabled: undefined };
    }
    const record = parsed as { batch_id?: unknown; disabled?: unknown };
    return {
      delivered: typeof record.batch_id === "string",
      disabled: "disabled" in record ? record.disabled === true : undefined,
    };
  } catch {
    return { delivered: false, disabled: undefined };
  }
}
